import React from 'react';
import { Alert, Button, Card, Form, Input, Skeleton, Typography, notification } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { PricingAPI } from '../../controller/PricingAPI';

// Laravel 欄位驗證失敗時，data.message 一律是制式的 "The given data was invalid."，
// 真正有用的訊息在 data.errors 裡，取第一筆顯示。與 UserPage.jsx 的同名輔助函式同一套規則。
function errorMessage(error) {
  const data = error?.response?.data;
  const errors = data?.errors;
  if (errors) {
    const firstMessages = Object.values(errors)[0];
    if (Array.isArray(firstMessages) && firstMessages.length > 0) {
      return firstMessages[0];
    }
  }
  return data?.message || '請確認欄位內容';
}

export default function InvitePage() {
  const { token } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(true);
  const [invitation, setInvitation] = React.useState(null);
  // 只有 404 代表連結真的不能用；429 (節流) 或網路錯誤都只是暫時打不到，
  // 若籠統顯示成「連結無效」，使用者照著提示重新申請一份邀請只會再次
  // 撞上同一個以 IP 為鍵的節流，得到一模一樣的失敗
  const [failure, setFailure] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    PricingAPI.invitations.verify(token)
      .then((response) => setInvitation(response.data))
      .catch((error) => {
        setFailure(error?.response?.status === 404 ? 'invalid' : 'unavailable');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await PricingAPI.invitations.accept(token, values);
      notification.success({
        message: '帳號已建立',
        description: '請使用你剛設定的密碼登入',
      });
      history.push('/');
    } catch (error) {
      notification.error({
        message: '建立失敗',
        description: errorMessage(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Card style={{ maxWidth: 460, margin: '80px auto' }}><Skeleton active /></Card>;
  }

  if (failure === 'unavailable') {
    return (
      <Card style={{ maxWidth: 460, margin: '80px auto' }}>
        <Alert
          type="warning"
          message="系統暫時無法回應"
          description="請稍後再試一次；若持續發生，請聯絡管理員。"
          showIcon
        />
      </Card>
    );
  }

  if (!invitation) {
    return (
      <Card style={{ maxWidth: 460, margin: '80px auto' }}>
        <Alert
          type="error"
          message="邀請連結無效或已過期"
          description="請聯絡管理員重新發送一份邀請。"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card title="加入禾風新棧決策支援系統" style={{ maxWidth: 460, margin: '80px auto' }}>
      <Typography.Paragraph>
        你被邀請以 <strong>{invitation.role_label}</strong> 的身分加入，帳號為 <strong>{invitation.email}</strong>。
        設定密碼後即可開始使用。
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="你的姓名"
          name="name"
          rules={[{ required: true, message: '請輸入姓名' }]}
        >
          <Input autoComplete="name" />
        </Form.Item>
        <Form.Item
          label="設定密碼"
          name="password"
          rules={[
            { required: true, message: '請輸入密碼' },
            { min: 8, message: '密碼至少 8 碼' },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          label="再次輸入密碼"
          name="password_confirmation"
          dependencies={['password']}
          rules={[
            { required: true, message: '請再次輸入密碼' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('兩次輸入的密碼不一致'));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting} block>
          建立帳號
        </Button>
      </Form>
    </Card>
  );
}
